import type { RepertoireLine, NodeRef } from '../api/types';
import { nodeKey } from '../state/route';
import { nodeInfo } from '../mocks/fixtures';
import type { ProposedApi } from './api';
import type { NodeAnnotation, NodeCounts, NodeKey } from './types';

/**
 * In-memory implementation of the proposed Wave-4 surface. Counts are computed
 * from the §4 mock fixtures (mirroring the aggregates the backend would serve);
 * annotations live in memory. Clearly mock-only.
 */
export class MockProposedApi implements ProposedApi {
  private readonly annotations = new Map<NodeKey, NodeAnnotation>();

  constructor() {
    // Seed one note so the surface has something to show in the demo.
    const transposition: NodeRef = {
      hashVersion: 1,
      zobristHash: '6'.padStart(32, '0'),
      positionId: '6',
    };
    this.annotations.set(nodeKey(transposition.zobristHash, transposition.positionId), {
      node: transposition,
      text: 'The key Caro-Kann transposition — both move orders meet here.',
      updatedAt: '2025-01-03T00:00:00Z',
    });
  }

  async getLineCounts(line: RepertoireLine): Promise<Map<NodeKey, NodeCounts>> {
    const counts = new Map<NodeKey, NodeCounts>();
    for (const node of line.nodes) {
      const info = nodeInfo(node.positionId);
      counts.set(nodeKey(node.zobristHash, node.positionId), {
        incomingPathCount: info.incomingPathCount,
        outgoingCount: info.outgoing.length,
      });
    }
    return counts;
  }

  async getAnnotation(node: NodeRef): Promise<NodeAnnotation | null> {
    return this.annotations.get(nodeKey(node.zobristHash, node.positionId)) ?? null;
  }

  async saveAnnotation(node: NodeRef, text: string): Promise<NodeAnnotation> {
    const annotation: NodeAnnotation = { node, text, updatedAt: new Date().toISOString() };
    this.annotations.set(nodeKey(node.zobristHash, node.positionId), annotation);
    return annotation;
  }
}
